import { AsyncPipe } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { NavigationEnd, Router, RouterEvent, Event, RouterOutlet } from '@angular/router';
import { NgbConfig } from '@ng-bootstrap/ng-bootstrap';
import { Hotkey, HotkeysService, HotkeyModule } from 'angular2-hotkeys';

import { LoadingComponent } from './shared/loading/loading.component';

import { BrandService } from '@app/services/brand.service';
import { DeviceService } from '@app/services/device.service';
import { GlobalService } from '@app/services/global.service';
import { ModalService } from '@app/services/modal.service';
import { ReleaseService } from '@app/services/release.service';
import { SettingService } from '@app/services/setting.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [HotkeyModule, LoadingComponent, RouterOutlet, AsyncPipe],
})
export class AppComponent implements OnInit, OnDestroy {
  private ngbConfig = inject(NgbConfig);
  private globalService = inject(GlobalService);
  private settingService = inject(SettingService);
  private releaseService = inject(ReleaseService);
  private router = inject(Router);
  private renderer2 = inject(Renderer2);
  private hotkeysService = inject(HotkeysService);
  private deviceService = inject(DeviceService);
  private modalService = inject(ModalService);
  private brandService = inject(BrandService);

  hotKeys: (Hotkey | Hotkey[])[] = [];

  loadingBusy$ = this.globalService.loadingBusyAfter400$;
  loadingText$ = this.globalService.loadingText$;

  // Observers.

  private routerEventsObserver = {
    next: (val: Event | RouterEvent) => {
      if (val instanceof NavigationEnd) {
        setTimeout(() => {
          // fix: checkmarks initially visible until the animation completes
          const elements = document.getElementsByClassName('preload');
          Array.prototype.forEach.call(elements, (el: Element) => {
            el.classList.remove('preload');
          });
        }, 500); // assuming that all animations that require fix complete in less than 500ms

        // add class to the body element that makes disctinction between routes that render trace graph and those that do not
        // this is useful for showing toastr messages with more offset for tuning and playground routes
        if (val.url.match(/(tuning|playground)/)) {
          this.renderer2.addClass(document.body, 'renders-trace-graph');
        } else {
          this.renderer2.removeClass(document.body, 'renders-trace-graph');
        }
      }
    },
  };

  constructor() {
    this.brandService.applyBrand();
    this.ngbConfig.animation = false;
    this.resolveQueryParams();
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeunloadHandler() {
    this.deviceService.requestStopAllDevices();
    if (this.router.url.startsWith('/devices')) {
      sessionStorage.setItem('navigateToUrlAfterPageReload', this.router.url);
    }
  }

  @HostListener('window:popstate', ['$event'])
  onPopState() {
    this.modalService.dismissAll();
  }

  ngOnInit() {
    this.globalService.loadDockerEngineApiEnabled();
    this.globalService.loadOblacDrivesUpdateServiceVersion();
    this.globalService.loadEncoderConfigurationUsage();

    this.releaseService.loadReleases();

    this.hotKeys.push(
      this.hotkeysService.add(
        new Hotkey(
          'ctrl+`',
          (): boolean => {
            this.settingService.toggleExpertMode();
            return false;
          },
          ['INPUT', 'SELECT', 'TEXTAREA'],
        ),
      ),
    );

    this.hotKeys.push(
      this.hotkeysService.add(
        new Hotkey(
          'ctrl+,',
          (): boolean => {
            this.settingService.toggleExpertMode();
            return false;
          },
          ['INPUT', 'SELECT', 'TEXTAREA'],
        ),
      ),
    );

    this.hotKeys.push(
      this.hotkeysService.add(
        new Hotkey('u n s a f e', (): boolean => {
          this.settingService.toggleUnsafeMode();
          return false;
        }),
      ),
    );

    this.hotKeys.push(
      this.hotkeysService.add(
        new Hotkey('alt+n', (): boolean => {
          navigator.clipboard.writeText(window.location.hostname);
          return false;
        }),
      ),
    );

    this.router.events.subscribe(this.routerEventsObserver);
  }

  ngOnDestroy() {
    this.hotKeys.forEach((k) => this.hotkeysService.remove(k));
  }

  resolveQueryParams() {
    const searchParams = new URLSearchParams(window.location.search);

    const text = searchParams.get('backButtonText');
    const url = searchParams.get('backButtonUrl');

    if (text && url) {
      this.settingService.updateSettingState({ backButton: { url, text } });
    }
  }
}
